import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { SiteLayout, useSettings } from "@/components/SiteLayout";
import { useI18n } from "@/lib/i18n";
import { api, rp, fmtDate, CUSTOMER_TYPES } from "@/lib/api";
import { PackageCard } from "@/pages/Landing";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { logError } from "@/lib/log";
import { useMember } from "@/lib/member";
import { openNativeDatePicker } from "@/lib/datePicker";

const dayIdx = (d) => { const w = new Date(d).getDay(); return w === 0 ? 6 : w - 1; };
const isMonday = (d) => new Date(`${d}T00:00:00`).getDay() === 1;
const isFriday = (d) => new Date(`${d}T00:00:00`).getDay() === 5;
const nextMonday = (value) => {
  const date = new Date(`${value}T00:00:00`);
  const offset = (8 - date.getDay()) % 7;
  date.setDate(date.getDate() + offset);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const nextFriday = (value) => {
  const date = new Date(`${value}T00:00:00`);
  const offset = (12 - date.getDay()) % 7;
  date.setDate(date.getDate() + offset);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const isWeekendPackage = (pkg) => (
  pkg?.duration_type === "weekend_block"
  || Number(pkg?.requires_weekday) === 4
  || /\bweekend\b/i.test(`${pkg?.name || ""} ${pkg?.fixed_block || ""}`)
);
const alignedPackageDate = (pkg, date) => {
  if (pkg?.duration_type === "weekday_block" && !isMonday(date)) {
    return nextMonday(date);
  }
  if (isWeekendPackage(pkg) && !isFriday(date)) {
    return nextFriday(date);
  }
  return date;
};
const localDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};
const localTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};
const fmtWhen = (iso, tz) => (iso
  ? `${new Date(iso).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · ${iso.slice(11, 16)} ${tz || "WIB"}`
  : "—");

const Field = ({ label, children, required = false }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-soft">
      {label}
      {required && <span className="ml-1 text-lm-red">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default function Booking() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const s = useSettings();
  const { member } = useMember();
  const [step, setStep] = useState(1);
  const [pkgs, setPkgs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [addons, setAddons] = useState([]);
  const [avail, setAvail] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [quote, setQuote] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sources, setSources] = useState([]);
  const [branches, setBranches] = useState([]);
  const [hub, setHub] = useState(null);
  const [hubs, setHubs] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [f, setF] = useState({
    rental_date: localDate(),
    start_time: localTime(), package_id: "", quantity: 1, service_mode: "pickup",
    delivery_area_id: "", recipient_name: "", address: "", area: "", district: "", city: "Jakarta",
    vehicle_type: "car",
    postal_code: "", maps_link: "", location_notes: "", addon_ids: [], promo_code: null,
    use_reward: false, use_free_days: 0, branch_id: "",
    customer_name: "", customer_whatsapp: "", customer_instagram: "", customer_email: "",
    customer_type: "Personal", notes: "", source: "",
    refund_bank_name: "", refund_account_number: "", refund_account_holder_name: "",
  });
  const set = useCallback((k, v) => setF((p) => ({ ...p, [k]: v })), []);
  const latest = useRef(f);
  const pickerOpenedFromPointer = useRef(false);
  latest.current = f;

  useEffect(() => {
    api.get("/public/packages")
      .then((r) => { setPkgs(r.data); if (r.data[0]) set("package_id", r.data[0].id); })
      .catch((e) => logError("Failed to load packages", e));
    api.get("/delivery-areas").then((r) => setAreas(r.data)).catch((e) => logError("Failed to load areas", e));
    api.get("/addons", { params: { public: 1 } }).then((r) => setAddons(r.data)).catch((e) => logError("Failed to load add-ons", e));
    api.get("/booking-sources").then((r) => setSources(r.data)).catch(() => setSources([]));
    api.get("/public/hubs").then((r) => {
      setHubs(r.data);
      setBranches(r.data);
      // Hub utama (paling banyak meja) jadi pilihan awal supaya angka ketersediaan yang tampil
      // selalu cocok dengan Hub yang tertulis di dropdown.
      const main = [...r.data].sort((a, b) => (b.units_total || 0) - (a.units_total || 0))[0];
      if (main) set("branch_id", main.id);
    }).catch(() => { setHubs([]); setBranches([]); });
  }, [set]);

  useEffect(() => {
    if (!member) {
      setSavedAddresses([]);
      return;
    }
    api.get("/member/addresses")
      .then((response) => setSavedAddresses(response.data))
      .catch((error) => logError("Gagal memuat alamat tersimpan", error));
  }, [member]);

  useEffect(() => {
    if (!f.branch_id) return;
    api.get(`/public/hubs/${f.branch_id}/schedule`).then((r) => setHub(r.data)).catch(() => setHub(null));
  }, [f.branch_id]);

  const pkg = useMemo(() => pkgs.find((p) => p.id === f.package_id), [pkgs, f.package_id]);

  useEffect(() => {
    if (!f.package_id || !f.rental_date || !f.start_time || !f.branch_id) return;
    api.post("/availability", { rental_date: f.rental_date, start_time: f.start_time, package_id: f.package_id, quantity: f.quantity, branch_id: f.branch_id, service_mode: f.service_mode })
      .then((r) => setAvail(r.data))
      .catch((e) => { logError("Availability check failed", e); setAvail(null); });
  }, [f.package_id, f.rental_date, f.start_time, f.quantity, f.branch_id, f.service_mode]);

  useEffect(() => {
    if (!f.delivery_area_id) { setEstimate(null); return; }
    // Kept for the area/distance label only — the public Delivery mode never charges a courier fee.
    api.get("/delivery-estimate", { params: { area_id: f.delivery_area_id, quantity: f.quantity } })
      .then((r) => setEstimate(r.data))
      .catch((e) => { logError("Delivery estimate failed", e); setEstimate(null); });
  }, [f.delivery_area_id, f.quantity]);

  const refreshQuote = useCallback(async (payload) => {
    try {
      const r = await api.post("/quote", payload);
      setQuote(r.data);
      return r.data;
    } catch (e) {
      logError("Quote failed", e);
      setQuote(null);
      return null;
    }
  }, []);

  // One concern: whenever any priced field changes, re-quote from the server.
  const pricedFields = useMemo(() => ({
    rental_date: f.rental_date, start_time: f.start_time, package_id: f.package_id, quantity: f.quantity,
    delivery_type: undefined, service_mode: f.service_mode, delivery_area_id: f.delivery_area_id, vehicle_type: f.vehicle_type,
    addon_ids: f.addon_ids, promo_code: f.promo_code, customer_whatsapp: f.customer_whatsapp,
    use_reward: f.use_reward, use_free_days: f.use_free_days, branch_id: f.branch_id || undefined,
  }), [f.rental_date, f.start_time, f.package_id, f.quantity, f.service_mode, f.delivery_area_id,
    f.vehicle_type, f.addon_ids, f.promo_code, f.customer_whatsapp, f.use_reward, f.use_free_days, f.branch_id]);

  useEffect(() => {
    if (!pricedFields.package_id || !pricedFields.branch_id) return;
    refreshQuote({
      ...pricedFields,
      customer_name: latest.current.customer_name || "quote",
      customer_whatsapp: latest.current.customer_whatsapp || "0000000000",
    });
  }, [pricedFields, refreshQuote]);

  const lookupCustomer = async (wa) => {
    if (!wa || wa.length < 8) return;
    try {
      const r = await api.get("/public/customer-lookup", { params: { whatsapp: wa } });
      if (r.data.found) {
        setF((p) => ({ ...p, customer_name: p.customer_name || r.data.name, customer_email: p.customer_email || r.data.email,
          customer_instagram: p.customer_instagram || r.data.instagram, customer_type: r.data.customer_type || p.customer_type,
          address: p.address || r.data.address }));
        toast.success(`${t("welcomeBack")} ${r.data.name}`);
      }
    } catch (e) {
      logError("Customer lookup failed", e);
    }
  };

  const applySavedAddress = (addressId) => {
    const saved = savedAddresses.find((item) => item.id === addressId);
    if (!saved) return;
    const deliveryArea = areas.find((item) => item.area_name === saved.area);
    setF((previous) => ({
      ...previous,
      recipient_name: saved.recipient_name || previous.recipient_name || member?.name || "",
      address: saved.address || "",
      area: saved.area || "",
      district: saved.district || "",
      city: saved.city || "",
      postal_code: saved.postal_code || "",
      maps_link: saved.maps_link || "",
      location_notes: saved.notes || "",
      delivery_area_id: deliveryArea?.id || previous.delivery_area_id,
    }));
  };

  const packageCta = useMemo(() => ({ select: t("select"), selected: t("selected") }), [t]);
  const isVenue = f.service_mode === "play_at_venue";
  const selectedHub = hubs.find((x) => x.id === f.branch_id) || null;
  const hubContactMissing = !selectedHub || selectedHub.contact_configured === false;
  const summaryServiceLabel = f.service_mode === "customer_arranged_delivery"
    ? (lang === "id"
      ? "Diurus customer (ambil & kembalikan di Hub)"
      : "Customer-arranged (pickup & return at Hub)")
    : quote?.service_mode_label || "-";
  const pkgPolicy = pkg?.service_policy === "lapak_delivery" ? "lapak_delivery" : "customer_arranged";
  const needsAddress = pkgPolicy === "lapak_delivery";
  const hubModes = useMemo(() => selectedHub?.service_modes
    || hub?.service_modes
    || ["customer_arranged_delivery"], [selectedHub, hub]);

  // Jam mulai diputuskan scheduler Hub; browser hanya menampilkan hasil yang read-only.
  const [slots, setSlots] = useState(null);
  useEffect(() => {
    if (!f.rental_date) return;
    api.get("/public/dispatch-slots", { params: { rental_date: f.rental_date, branch_id: f.branch_id || undefined, package_id: f.package_id || undefined } })
      .then((r) => setSlots(r.data)).catch(() => setSlots(null));
  }, [f.rental_date, f.branch_id, f.package_id, f.quantity, f.service_mode]);

  useEffect(() => {
    const list = slots?.slots || [];
    if (list.length && f.start_time !== list[0]) set("start_time", list[0]);
  }, [slots, f.start_time, set]);

  const rentalDateHasError = Boolean(
    avail?.blocked || (slots?.errors || []).length > 0,
  );

  useEffect(() => {
    // Paket menentukan layanannya: diantar LapakMahjong.id atau diurus customer sendiri.
    const want = needsAddress ? "lapak_delivery" : "customer_arranged_delivery";
    if (f.service_mode !== want) set("service_mode", want);
  }, [needsAddress, f.service_mode, set]);
  const hubDay = hub?.hours?.[dayIdx(f.rental_date)];
  const closeTime = avail?.hub_close || hubDay?.close || s?.close_time || "22:00";
  const openTime = avail?.hub_open || hubDay?.open || "10:00";
  const endsAfterClose = useCallback((p) => {
    const [sh, sm] = (f.start_time || "00:00").split(":").map(Number);
    const [ch, cm] = (closeTime || "22:00").split(":").map(Number);
    const mins = (p.duration_type === "hour" || p.duration_type === "overnight")
      ? p.duration_value * 60 : p.duration_value * 1440;
    return sh * 60 + sm + mins > ch * 60 + cm;
  }, [f.start_time, closeTime]);
  const packageDateNeedsShift = useCallback((p) => (
    alignedPackageDate(p, f.rental_date) !== f.rental_date
  ), [f.rental_date]);
  const venueBlocked = useCallback((p) => isVenue && endsAfterClose(p), [isVenue, endsAfterClose]);
  const selectPackage = useCallback((x) => {
    if (venueBlocked(x)) {
      toast.error(`${t("notAvailable")} — ${closeTime}`);
      return;
    }
    set("package_id", x.id);
    const alignedDate = alignedPackageDate(x, f.rental_date);
    if (alignedDate !== f.rental_date) {
      set("rental_date", alignedDate);
      toast.success(
        x.duration_type === "weekday_block"
          ? "Jadwal Paket Weekdays otomatis dipindahkan ke Senin terdekat."
          : "Jadwal Paket Weekend otomatis dipindahkan ke Jumat terdekat.",
      );
    }
  }, [set, venueBlocked, closeTime, t, f.rental_date]);

  useEffect(() => {
    const cur = pkgs.find((p) => p.id === f.package_id);
    const alignedDate = alignedPackageDate(cur, f.rental_date);
    if (cur && alignedDate !== f.rental_date) {
      set("rental_date", alignedDate);
    }
  }, [pkgs, f.package_id, f.rental_date, set]);

  const updateRentalDate = useCallback((value) => {
    const alignedDate = alignedPackageDate(pkg, value);
    set("rental_date", alignedDate);
    if (alignedDate !== value) {
      toast.info(
        pkg?.duration_type === "weekday_block"
          ? "Paket Weekdays selalu dimulai pada Senin."
          : "Paket Weekend selalu dimulai pada Jumat.",
      );
    }
  }, [pkg, set]);

  const openDatePickerFromPointer = useCallback((event) => {
    if (openNativeDatePicker(event.currentTarget)) {
      pickerOpenedFromPointer.current = true;
      event.preventDefault();
      window.requestAnimationFrame(() => {
        pickerOpenedFromPointer.current = false;
      });
    }
  }, []);

  const openDatePickerFromClick = useCallback((event) => {
    if (pickerOpenedFromPointer.current) {
      return;
    }
    openNativeDatePicker(event.currentTarget);
  }, []);

  const openDatePickerFromKeyboard = useCallback((event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    if (openNativeDatePicker(event.currentTarget)) {
      event.preventDefault();
    }
  }, []);

  useEffect(() => {
    if (!isVenue || !pkgs.length) return;
    const cur = pkgs.find((p) => p.id === f.package_id);
    if (cur && !endsAfterClose(cur)) return;
    const fit = pkgs.find((p) => !endsAfterClose(p));
    if (fit) set("package_id", fit.id);
  }, [isVenue, pkgs, f.package_id, f.start_time, endsAfterClose, set]);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    const base = quote ? quote.rental_subtotal + quote.addon_total + quote.delivery_fee + quote.pickup_fee : 0;
    try {
      const r = await api.post("/promo/validate", {
        code: promoInput, subtotal: base, whatsapp: f.customer_whatsapp,
        delivery_fee: (quote?.delivery_fee || 0) + (quote?.pickup_fee || 0),
        package_id: avail?.package_id || f.package_id,
      });
      set("promo_code", r.data.promo_code);
      toast.success(`${r.data.promo_name}: -${rp(r.data.discount)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid promo code");
    }
  };

  const submit = async () => {
    if (!agree) return toast.error("Anda harus menyetujui Syarat dan Ketentuan sebelum memesan");
    if (quote?.hub_errors?.length) return toast.error(quote.hub_errors[0]);
    setBusy(true);
    try {
      const r = await api.post("/bookings", {
        ...f, delivery_type: undefined, terms_accepted: true, terms_version: "2.0", marketing_opt_in: marketing,
      });
      nav(`/booking/${r.data.booking_code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  const stepsLabels = [t("package"), t("schedule"), t("service"), t("details"), t("review")];
  const canNext = () => {
    if (step === 1) return !!f.package_id && !venueBlocked(pkg);
    if (step === 2) return f.rental_date && avail?.ok;
    if (step === 3) {
      if (hubContactMissing) return false;
      return !needsAddress || (f.address && f.delivery_area_id);
    }
    if (step === 4) {
      if (!(f.customer_name && f.customer_whatsapp.length >= 8)) return false;
      if ((quote?.deposit_amount || 0) > 0) {
        return f.refund_bank_name.trim().length >= 2
          && f.refund_account_number.replace(/\D/g, "").length >= 6
          && f.refund_account_holder_name.trim().length >= 3;
      }
      return true;
    }
    return true;
  };

  const Summary = ({ showPayment = step === 5 }) => (
    <div className="space-y-1.5 text-sm" data-testid="booking-summary">
      <Row l={t("rentalDate")} v={fmtDate(f.rental_date, lang)} />
      <Row l={t("automaticRentalStartLabel")} testid="sum-dispatch"
        v={fmtWhen(avail?.start_datetime, avail?.tz_label)} />
      {(step === 2 || step === 5) && (
        <p data-testid="summary-automatic-start-note" className="text-xs text-ink-soft">
          {t("automaticRentalStartNote")}
        </p>
      )}
      <Row l={t("returnDue")} testid="sum-return-due"
        v={fmtWhen(quote?.operational_return_due || avail?.operational_return_due, avail?.tz_label)} />
      <Row l={t("service")} v={summaryServiceLabel} testid="sum-service-mode" />
      {quote?.nominal_package_end && (
        <Row l={lang === "id" ? "Paket berakhir" : "Package ends"} testid="sum-nominal-end"
          v={`${quote.nominal_package_end.slice(11, 16)} (${new Date(quote.nominal_package_end).toLocaleDateString("en-GB")})`} />
      )}
      {quote?.operational_return_due && quote.operational_return_due !== quote.nominal_package_end && (
        <Row l={t("returnDue")} testid="sum-operational-due"
          v={`${quote.operational_return_due.slice(11, 16)} (${new Date(quote.operational_return_due).toLocaleDateString("en-GB")})`} />
      )}
      <Row l={t("package")} v={quote?.package_name_snapshot || pkg?.name || "-"} />
      {quote?.auto_upgraded_from && (
        <div className="text-xs text-amber-700" data-testid="sum-upgrade-note">
          {quote.auto_upgraded_from} melewati jam tutup {closeTime} — dihitung paket menginap {quote.package_name_snapshot}
        </div>
      )}
      {(quote?.hub_warnings || []).map((w) => (
        <div key={w} className="text-xs text-ink-soft" data-testid="sum-hub-warning">{w}</div>
      ))}
      {(quote?.hub_errors || []).map((w) => (
        <div key={w} className="text-xs text-lm-red font-medium" data-testid="sum-hub-error">{w}</div>
      ))}
      <Row l={t("units")} v={f.quantity} />
      <div className="h-px bg-black/10 my-2" />
      <Row l="Rental" v={rp(quote?.rental_subtotal || 0)} testid="sum-rental" />
      {quote?.addon_total > 0 && <Row l={t("addons")} v={rp(quote.addon_total)} />}
      {quote?.delivery_fee > 0 && <Row l={`Delivery${quote.vehicle_type === "bike" ? " (Bike)" : quote.vehicle_type === "car" ? " (Car)" : ""}`} v={rp(quote.delivery_fee)} testid="sum-delivery" />}
      {quote?.pickup_fee > 0 && <Row l="Pickup" v={rp(quote.pickup_fee)} />}
      {quote?.promo_discount > 0 && <Row l={`Promo ${quote.promo_code}`} v={`-${rp(quote.promo_discount)}`} accent testid="sum-promo" />}
      {quote?.pickup_discount > 0 && <Row l={quote.pickup_discount_label || "Self Pickup Discount"} v={`-${rp(quote.pickup_discount)}`} accent testid="sum-pickup-discount" />}
      {quote?.loyalty_discount > 0 && <Row l={`${quote.loyalty_tier} Member -${quote.loyalty_discount_percent}%`} v={`-${rp(quote.loyalty_discount)}`} accent testid="sum-loyalty-discount" />}
      {quote?.benefit_discount > 0 && <Row l={quote.benefit_label || "Benefit"} v={`-${rp(quote.benefit_discount)}`} accent testid="sum-benefit-discount" />}
      <div className="h-px bg-black/10 my-2" />
      <Row l="Rental Total" v={rp(quote?.final_rental_total || 0)} bold testid="sum-total" />
      {quote?.deposit_amount > 0 && <Row l={t("refundableDeposit")} v={rp(quote.deposit_amount)} testid="sum-deposit" />}
      {showPayment && (
        <>
          <div className="h-px bg-black/10 my-2" />
          <div className="flex justify-between items-baseline">
            <span className="text-xs uppercase tracking-widest font-semibold">
              {t("totalPayment")}
            </span>
            <span className="font-display text-2xl text-lm-red" data-testid="sum-grand-total">
              {rp(quote?.total_payment || 0)}
            </span>
          </div>
          {quote?.deposit_amount > 0 && (
            <p className="pt-2 text-xs text-ink-soft">{t("depositNote")}</p>
          )}
        </>
      )}
    </div>
  );

  return (
    <SiteLayout>
      <div className="mx-auto max-w-6xl px-5 py-10 pb-32 lg:pb-16">
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="progress-indicator">
          {stepsLabels.map((l, i) => (
            <React.Fragment key={l}>
              <button data-testid={`booking-step-${i + 1}`} onClick={() => i + 1 < step && setStep(i + 1)}
                className={`px-3 py-1.5 rounded-full border ${step === i + 1 ? "bg-lm-red text-white border-lm-red" : step > i + 1 ? "border-lm-red/40 text-lm-red" : "border-black/10 text-ink-soft"}`}>
                {step > i + 1 ? <Check size={12} className="inline mr-1" /> : null}{l}
              </button>
              {i < 4 && <span className="text-ink-soft/40">→</span>}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-8 grid lg:grid-cols-[1.6fr_1fr] gap-10 items-start">
          <div className="lm-fade">
            {step === 2 && (
              <div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight">{t("whenPlay")}</h1>
                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                  <Field label={t("rentalDate")}>
                    <Input type="date" data-testid="input-date" value={f.rental_date}
                      min={hub?.hub_local_today || localDate()}
                      onChange={(e) => updateRentalDate(e.target.value)}
                      onPointerDown={openDatePickerFromPointer}
                      onClick={openDatePickerFromClick}
                      onKeyDown={openDatePickerFromKeyboard}
                      aria-invalid={rentalDateHasError}
                      className={[
                        "h-12 bg-white px-3 text-sm font-medium text-ink",
                        "border-black/15 hover:border-black/30",
                        "focus-visible:ring-2 focus-visible:ring-lm-red focus-visible:ring-offset-2",
                        "disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-ink-soft",
                        rentalDateHasError ? "border-lm-red focus-visible:ring-lm-red" : "",
                      ].join(" ")} />
                    <p data-testid="rental-date-hint" className="mt-1 text-xs text-ink-soft">
                      {t("rentalDateHint")}
                    </p>
                  </Field>
                  {hubs.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-widest text-ink-soft">{t("hubLabel")}</span>
                        {avail && (
                          <span data-testid="hub-availability-inline" className="text-xs font-semibold text-emerald-700">
                          {t("unitsAvailable", {
                            count: `${avail.available_units}/${avail.total_units}`,
                          })}
                          </span>
                        )}
                      </div>
                      <select data-testid="hub-picker" value={f.branch_id || ""} onChange={(e) => set("branch_id", e.target.value)}
                        className="mt-1.5 w-full h-12 rounded-md border border-input bg-white px-3">
                        {hubs.map((b) => (
                          <option key={b.id} value={b.id} data-testid={`hub-option-${b.code}`}>
                            {b.name} · {b.tz_label}
                          </option>
                        ))}
                      </select>
                      {avail?.blocked && <p className="mt-1 text-xs text-lm-red">{t("dateUnavailableForHub")}</p>}
                    </div>
                  )}
                  <Field label={t("automaticRentalStartLabel")}>
                    <div
                      data-testid="automatic-start-time"
                      className="flex h-12 items-center rounded-md border border-input bg-neutral-50 px-3 font-semibold"
                    >
                      {slots?.start_time || avail?.automatic_start_time || "—"}
                      {(slots?.tz_label || avail?.tz_label) && ` ${slots?.tz_label || avail?.tz_label}`}
                    </div>
                    <p className="mt-1.5 text-xs text-ink-soft" data-testid="automatic-start-note">
                      {t("automaticRentalStartNote")}
                    </p>
                    {(slots?.errors || []).map((error) => (
                      <p key={error} data-testid="automatic-start-error" className="mt-1.5 text-xs text-lm-red">
                        {error}
                      </p>
                    ))}
                    {(slots?.errors || []).length > 0 && slots?.suggested_rental_date && (
                      <button
                        data-testid="use-next-opening-date"
                        onClick={() => updateRentalDate(slots.suggested_rental_date)}
                        className="mt-2 text-xs font-semibold text-lm-red hover:underline"
                      >
                        {t("useNextOpeningDate", { date: fmtDate(slots.suggested_rental_date, lang) })}
                      </button>
                    )}
                  </Field>
                  <Field label={t("units")}>
                    <div className="flex items-center gap-3">
                      <button data-testid="qty-minus" onClick={() => set("quantity", Math.max(1, f.quantity - 1))}
                        className="w-12 h-12 rounded-md border border-black/15 text-lg">−</button>
                      <span data-testid="qty-value" className="w-12 text-center text-lg font-semibold">{f.quantity}</span>
                      <button data-testid="qty-plus" onClick={() => set("quantity", f.quantity + 1)}
                        className="w-12 h-12 rounded-md border border-black/15 text-lg">+</button>
                    </div>
                  </Field>
                </div>
                {avail && (
                  <div data-testid="fixed-schedule-box" className="mt-6 rounded-xl border border-black/10 bg-white p-5">
                    <div className="text-[11px] uppercase tracking-widest text-ink-soft">
                      {t("schedule")}
                    </div>
                    <div className="mt-2 space-y-1 text-sm">
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="text-ink-soft">{t("automaticRentalStartLabel")}</span>
                        <span className="font-semibold" data-testid="schedule-dispatch">
                          {fmtWhen(avail.start_datetime, avail.tz_label)}
                        </span>
                      </div>
                      <div className="flex flex-wrap justify-between gap-2">
                        <span className="text-ink-soft">{t("returnDue")}</span>
                        <span className="font-semibold" data-testid="schedule-return">
                          {fmtWhen(avail.operational_return_due, avail.tz_label)}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-ink-soft" data-testid="fixed-schedule-note">
                      {t("automaticRentalStartNote")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight">{t("selectPackage")}</h1>
                <p className="mt-2 text-sm text-ink-soft">
                  {isVenue
                    ? `Kirim ${avail?.dispatch_time || "20:00"} ${avail?.tz_label || "WIB"} · Main di Venue harus selesai sebelum Hub tutup ${closeTime}.`
                    : "Pengiriman dan pengembalian Portable Mahjong mengikuti jam operasional masing-masing cabang."}
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                  {pkgs.map((p) => (
                    <div key={p.id} className={`relative ${venueBlocked(p) ? "opacity-45" : ""}`}>
                      <PackageCard p={p} selected={p.id === f.package_id} onSelect={selectPackage} ctaLabel={packageCta}
                        disabled={venueBlocked(p)} disabledLabel="Tidak tersedia" />
                      {packageDateNeedsShift(p) && (
                        <p className="mt-1.5 text-xs text-lm-red" data-testid={`package-date-shift-${p.id}`}>
                          {p.duration_type === "weekday_block"
                            ? "Pilih paket ini untuk otomatis mulai Senin terdekat."
                            : "Pilih paket ini untuk otomatis mulai Jumat terdekat."}
                        </p>
                      )}
                      {venueBlocked(p) && (
                        <p className="mt-1.5 text-xs text-lm-red" data-testid={`package-venue-blocked-${p.id}`}>
                          Selesai setelah Hub tutup {closeTime} — tidak tersedia untuk Main di Venue
                        </p>
                      )}
                      {!isVenue && endsAfterClose(p) && p.duration_type === "hour"
                        && p.duration_value < (hub?.overnight_min_hours || 10) && (
                        <p className="mt-1.5 text-xs text-amber-700" data-testid={`package-day-notice-${p.id}`}>
                          Melewati jam tutup {closeTime} — dihitung paket menginap minimum
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-8 rounded-xl border border-lm-gold/40 bg-lm-gold/10 p-5"
                  data-testid="dm-cta-card">
                  <div className="text-[11px] uppercase tracking-widest text-ink-soft">
                    {t("needMore")}
                  </div>
                  <p className="mt-2 text-sm">
                    {s?.dm_cta_label || (lang === "id"
                      ? "Lebih banyak meja, event, atau kolaborasi? DM kami"
                      : "More units, events, or collaboration? DM us")}
                  </p>
                  <a data-testid="dm-cta-link" target="_blank" rel="noreferrer"
                    href={`https://wa.me/${s?.dm_cta_whatsapp || s?.whatsapp_number || "6281236810017"}?text=${encodeURIComponent(lang === "id" ? "Hai LapakMahjong, saya mau tanya soal event / lebih banyak meja / kolaborasi." : "Hi LapakMahjong, I'd like to ask about events / more units / collaboration.")}`}
                    className="mt-3 inline-block rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream transition-opacity hover:opacity-90">
                    {lang === "id" ? "Chat WhatsApp" : "Chat on WhatsApp"}
                  </a>                </div>
                {addons.length > 0 && (
                  <div className="mt-10">
                    <h2 className="text-base md:text-lg font-semibold">{t("addons")}</h2>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      {addons.map((a) => {
                        const on = f.addon_ids.includes(a.id);
                        return (
                          <button key={a.id} data-testid={`addon-${a.id}`}
                            onClick={() => set("addon_ids", on ? f.addon_ids.filter((x) => x !== a.id) : [...f.addon_ids, a.id])}
                            className={`text-left lm-card rounded-xl p-4 ${on ? "border-lm-red ring-1 ring-lm-red" : ""}`}>
                            <div className="flex justify-between gap-3">
                              <span className="font-medium">{a.name}</span><span className="text-lm-red">{rp(a.price)}</span>
                            </div>
                            <p className="text-xs text-ink-soft mt-1">{a.description}</p>
                            {a.requires_admin_confirmation && (
                              <p className="mt-1.5 text-[11px] font-semibold text-amber-700"
                                data-testid={`addon-confirm-note-${a.id}`}>
                                {t("addonAdminConfirm")}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight">{t("serviceQuestion")}</h1>
                <div className="mt-6 flex gap-3 flex-wrap" data-testid="service-mode-picker">
                  {[["pickup", t("serviceSelfPickup")],
                    ["lapak_delivery", t("serviceByLapak")],
                    ["customer_arranged_delivery", t("serviceByCustomer")]]
                    .filter(([v]) => v === (needsAddress ? "lapak_delivery" : "customer_arranged_delivery")
                      && hubModes.includes(v)).map(([v, l]) => (
                    <button key={v} data-testid={`service-mode-${v}`} onClick={() => set("service_mode", v)}
                      className={`px-5 py-3 rounded-full text-sm border transition-colors ${f.service_mode === v ? "bg-ink text-cream border-ink" : "border-black/15 hover:border-ink/40"}`}>{l}</button>
                  ))}
                </div>
                <p className="mt-3 text-sm text-ink-soft" data-testid="service-note">
                  {needsAddress ? t("serviceByLapakNote") : t("serviceByCustomerNote")}
                </p>
                {!needsAddress && (
                  <div className="mt-6 lm-card rounded-xl p-5" data-testid="self-pickup-info">
                    <div className="text-xs uppercase tracking-widest text-ink-soft">
                      {t("pickupLocation")} · {selectedHub?.name || "Hub"}
                    </div>
                    {hubContactMissing ? (
                      <div className="mt-2" data-testid="hub-contact-missing">
                        <p className="text-sm text-lm-red font-medium">
                          {t("hubNotConfigured", { hub: selectedHub?.name || "Hub", fields: (selectedHub?.missing_public_fields || []).join(", ") })}
                        </p>
                        <p className="mt-1.5 text-xs text-ink-soft">{t("hubNotConfiguredSub")}</p>
                      </div>
                    ) : (
                      <>
                        <div className="mt-2 text-base" data-testid="pickup-address">{selectedHub.address}</div>
                        <p className="mt-2 text-xs text-ink-soft">
                          {t("hubHoursNote", { open: openTime, close: closeTime, tz: avail?.tz_label || "WIB", last: avail?.latest_start || hub?.latest_pickup || "21:30" })}
                        </p>
                        <a href={selectedHub.maps_url} target="_blank" rel="noreferrer" data-testid="pickup-maps-link"
                          className="mt-3 block text-sm text-lm-red hover:underline">
                          {t("openMaps")}: {selectedHub.name} →
                        </a>
                        <a href={`https://wa.me/${String(selectedHub.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
                          data-testid="hub-whatsapp-link" className="mt-1.5 block text-sm text-lm-red hover:underline">
                          WhatsApp {selectedHub.name} →
                        </a>
                      </>
                    )}
                    {quote?.pickup_discount > 0 && (
                      <div className="mt-3 inline-block text-sm px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="pickup-discount-badge">
                        {quote.pickup_discount_label || "Self Pickup Discount"}: −{rp(quote.pickup_discount)}
                      </div>
                    )}
                  </div>
                )}
                {isVenue && (
                  <div className="mt-6 lm-card rounded-xl p-5" data-testid="play-at-venue-info">
                    <div className="text-xs uppercase tracking-widest text-ink-soft">Main di Venue</div>
                    <div className="mt-2 text-base">{s?.pickup_address || s?.address}</div>
                    <ul className="mt-3 space-y-1.5 text-sm text-ink-soft list-disc pl-5">
                      <li>Meja tidak dibawa keluar Hub — Anda bermain di tempat.</li>
                      <li>Sesi wajib selesai sebelum Hub tutup ({closeTime}) · tanpa perpanjangan menginap.</li>
                      <li>Tanpa deposit dan tanpa biaya kurir.</li>
                    </ul>
                  </div>
                )}
                {needsAddress && (
                  <>
                  {member && (
                    <div className="mt-6 border-y border-black/10 py-4" data-testid="saved-address-picker">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold">Alamat tersimpan</div>
                          <p className="mt-1 text-xs text-ink-soft">
                            Pilih alamat Member atau kelola beberapa alamat di area akun Anda.
                          </p>
                        </div>
                        <button data-testid="manage-saved-addresses" onClick={() => nav("/member/addresses")}
                          className="text-sm text-lm-red hover:underline">
                          Kelola alamat
                        </button>
                      </div>
                      {savedAddresses.length > 0 && (
                        <select data-testid="saved-address-select" defaultValue=""
                          onChange={(event) => applySavedAddress(event.target.value)}
                          className="mt-3 h-12 w-full rounded-md border border-input bg-white px-3 text-sm">
                          <option value="">Pilih alamat tersimpan</option>
                          {savedAddresses.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.label || item.address} · {item.area || item.city || "Alamat"}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <Field label={t("fullName")}><Input data-testid="input-recipient" className="h-12" value={f.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} /></Field>
                    <Field label={t("whatsapp")}><Input data-testid="input-delivery-wa" className="h-12" value={f.customer_whatsapp} onChange={(e) => set("customer_whatsapp", e.target.value)} /></Field>
                    <div className="sm:col-span-2">
                      <Field label={t("addrFull")}><Textarea data-testid="input-address" rows={3} value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
                    </div>
                    <Field label={t("addrArea")}>
                      <select data-testid="input-area" value={f.delivery_area_id} className="w-full h-12 rounded-md border border-input bg-white px-3"
                        onChange={(e) => { const a = areas.find((x) => x.id === e.target.value); set("delivery_area_id", e.target.value); set("area", a?.area_name || ""); }}>
                        <option value="">{t("choose")}</option>
                        {areas.map((a) => <option key={a.id} value={a.id}>{a.area_name}{a.distance_km ? ` · ±${a.distance_km} km` : ""}</option>)}
                      </select>
                    </Field>
                    <Field label={lang === "id" ? "Kecamatan" : "District"}><Input data-testid="input-district" className="h-12" value={f.district} onChange={(e) => set("district", e.target.value)} /></Field>
                    <Field label={lang === "id" ? "Kota" : "City"}><Input data-testid="input-city" className="h-12" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
                    <Field label={lang === "id" ? "Kode Pos" : "Postal Code"}><Input data-testid="input-postal" className="h-12" value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></Field>
                    <Field label={t("openMaps")}><Input data-testid="input-maps" className="h-12" value={f.maps_link} onChange={(e) => set("maps_link", e.target.value)} /></Field>
                    <div className="sm:col-span-2">
                      <Field label={lang === "id" ? "Catatan lokasi" : "Location Notes"}><Input data-testid="input-loc-notes" className="h-12" value={f.location_notes} onChange={(e) => set("location_notes", e.target.value)} /></Field>
                    </div>
                  </div>
                  {estimate?.area_name && (
                    <p className="mt-3 text-xs text-ink-soft" data-testid="delivery-estimate-note">
                      ±{estimate.distance_km} km → {estimate.area_name}
                    </p>
                  )}
                  </>
                )}
              </div>
            )}

            {step === 4 && (
              <div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight">{t("yourDetails")}</h1>
                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                  <Field label={t("fullName")}><Input data-testid="input-name" className="h-12" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></Field>
                  <Field label={t("whatsapp")}>
                    <Input data-testid="input-whatsapp" className="h-12" placeholder="08xxxxxxxxxx" value={f.customer_whatsapp}
                      onChange={(e) => set("customer_whatsapp", e.target.value)} onBlur={(e) => lookupCustomer(e.target.value)} />
                  </Field>
                  <Field label={t("instagramOpt")}><Input data-testid="input-ig" className="h-12" value={f.customer_instagram} onChange={(e) => set("customer_instagram", e.target.value)} /></Field>
                  <Field label={t("emailOpt")}><Input data-testid="input-email" className="h-12" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></Field>
                  <Field label={t("customerType")}>
                    <select data-testid="input-customer-type" value={f.customer_type} onChange={(e) => set("customer_type", e.target.value)}
                      className="w-full h-12 rounded-md border border-input bg-white px-3">
                      {CUSTOMER_TYPES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label={t("howFoundUs")}>
                    <select data-testid="input-source" value={f.source} onChange={(e) => set("source", e.target.value)}
                      className="w-full h-12 rounded-md border border-input bg-white px-3">
                      <option value="">{t("choose")}</option>
                      {sources.filter((x) => x !== "Admin").map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="mt-6">
                  <span className="text-xs uppercase tracking-widest text-ink-soft">{t("promoQ")}</span>
                  <div className="mt-2 flex gap-2">
                    <Input data-testid="input-promo" className="h-12" placeholder="LAPAK10" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} />
                    <button data-testid="apply-promo-btn" onClick={applyPromo} className="lm-btn-primary rounded-md px-6 text-sm font-semibold">{t("apply")}</button>
                  </div>
                </div>
                {(quote?.deposit_amount || 0) > 0 && (
                  <div className="mt-8 rounded-xl border border-lm-gold/40 bg-lm-gold/5 p-5" data-testid="refund-account-block">
                    <div className="text-sm font-semibold">{t("refundAccountTitle")}</div>
                    <p className="mt-1 text-xs text-ink-soft">
                      {t("refundAccountSub", { amount: rp(quote.deposit_amount) })}
                    </p>
                    <div className="mt-4 grid sm:grid-cols-3 gap-4">
                      <Field label={t("bankName")} required>
                        <Input data-testid="input-refund-bank" className="h-12" placeholder="BCA"
                          value={f.refund_bank_name} onChange={(e) => set("refund_bank_name", e.target.value)} />
                      </Field>
                      <Field label={t("accountNumber")} required>
                        <Input data-testid="input-refund-number" className="h-12" inputMode="numeric" placeholder="1234567890"
                          value={f.refund_account_number} onChange={(e) => set("refund_account_number", e.target.value)} />
                      </Field>
                      <Field label={t("accountHolder")} required>
                        <Input data-testid="input-refund-holder" className="h-12"
                          value={f.refund_account_holder_name} onChange={(e) => set("refund_account_holder_name", e.target.value)} />
                      </Field>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 5 && (
              <div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight">{t("yourBooking")}</h1>
                <div className="mt-6 lm-card rounded-xl p-6"><Summary showPayment /></div>
                {(quote?.benefits_available?.reward || quote?.benefits_available?.free_days_left > 0) && (
                  <div className="mt-6 rounded-xl border border-lm-gold/50 bg-lm-gold/5 p-5" data-testid="benefit-picker">
                    <div className="text-sm font-semibold">{t("benefitTitle")}</div>
                    <p className="mt-1 text-xs text-ink-soft">{t("benefitSub")}</p>
                    <div className="mt-4 space-y-2">
                      {quote.benefits_available.reward && (
                        <button data-testid="use-reward-toggle"
                          onClick={() => { set("use_free_days", 0); set("use_reward", !f.use_reward); }}
                          className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${f.use_reward ? "border-lm-red bg-white" : "border-black/15 hover:border-lm-red/50"}`}>
                          {t("useReward").replace("{n}", quote.benefits_available.reward_days || 1)}
                        </button>
                      )}
                      {quote.benefits_available.free_days_left > 0 && (
                        <button data-testid="use-free-days-toggle"
                          onClick={() => { set("use_reward", false); set("use_free_days", f.use_free_days > 0 ? 0 : 1); }}
                          className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${f.use_free_days > 0 ? "border-lm-red bg-white" : "border-black/15 hover:border-lm-red/50"}`}>
                          {t("useFreeDays").replace("{n}", quote.benefits_available.free_days_left)}
                        </button>
                      )}
                      {(f.use_reward || f.use_free_days > 0) && (
                        <button data-testid="clear-benefit" onClick={() => { set("use_reward", false); set("use_free_days", 0); }}
                          className="text-xs text-ink-soft hover:text-lm-red">{t("dontUseBenefit")}</button>
                      )}
                    </div>
                  </div>
                )}
                <div className="mt-6 lm-card rounded-xl p-5 text-sm space-y-1">
                  <div className="font-semibold">{f.customer_name}</div>
                  <div className="text-ink-soft">{f.customer_whatsapp}</div>
                  {f.service_mode === "customer_arranged_delivery" && <div className="text-ink-soft">{f.address} {f.area && `· ${f.area}`}</div>}
                </div>
                <label className="mt-6 flex items-start gap-3 text-sm">
                  <Checkbox data-testid="agree-terms" checked={agree} onCheckedChange={(v) => setAgree(!!v)} />
                  <span>{t("agreeTerms")}{" "}
                    <a href="/syarat-dan-ketentuan" target="_blank" rel="noreferrer"
                      data-testid="terms-link" className="text-lm-red underline">{t("viewTerms")}</a>
                    <span className="block mt-1 text-xs text-ink-soft">
                      Termasuk deposit Rp500.000 per set, biaya keterlambatan Rp100.000 per jam per set,
                      kebijakan pembatalan, dan Pelindungan Data Pribadi (versi 2.0).
                    </span>
                  </span>
                </label>
                <label className="mt-4 flex items-start gap-3 text-sm">
                  <Checkbox data-testid="agree-marketing" checked={marketing}
                    onCheckedChange={(v) => setMarketing(!!v)} />
                  <span className="text-ink-soft text-xs">
                    Opsional: saya bersedia menerima informasi promo dan produk baru LapakMahjong.id.
                  </span>
                </label>
              </div>
            )}
          </div>

          <aside className="hidden lg:block sticky top-24 lm-card rounded-xl p-6">
            <div className="text-xs uppercase tracking-widest text-ink-soft mb-4">{t("yourBooking")}</div>
            <Summary showPayment={step === 5} />
          </aside>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 glass px-5 py-3 flex items-center gap-3 lg:static lg:bg-transparent lg:backdrop-blur-none lg:max-w-6xl lg:mx-auto lg:border-0">
        {step > 1 && (
          <button data-testid="back-btn" onClick={() => setStep(step - 1)} className="px-6 py-3.5 rounded-full border border-black/15 text-sm">{t("back")}</button>
        )}
        {step === 5 && (
          <div className="flex-1 text-sm lg:hidden" data-testid="mobile-payment-total">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">
              {t("totalPayment")}
            </div>
            <div className="font-semibold text-lm-red" data-testid="mobile-total">
              {rp(quote?.total_payment || 0)}
            </div>
          </div>
        )}
        {step < 5 ? (
          <button data-testid="continue-btn" disabled={!canNext()} onClick={() => setStep(step + 1)}
            className="lm-btn-primary rounded-full px-8 py-3.5 text-sm font-semibold disabled:opacity-40 lg:flex-none flex-1">
            {t("continueBtn")}
          </button>
        ) : (
          <button data-testid="confirm-booking-btn" disabled={busy || !agree || (quote?.hub_errors || []).length > 0} onClick={submit}
            className="lm-btn-primary rounded-full px-8 py-3.5 text-sm font-semibold disabled:opacity-40 flex-1 lg:flex-none inline-flex items-center justify-center gap-2">
            {busy && <Loader2 size={15} className="animate-spin" />} {t("confirmBooking")}
          </button>
        )}
      </div>
    </SiteLayout>
  );
}

const Row = ({ l, v, bold, accent, testid }) => (
  <div className="flex justify-between gap-4">
    <span className="text-ink-soft">{l}</span>
    <span data-testid={testid} className={`${bold ? "font-semibold" : ""} ${accent ? "text-lm-red" : ""} text-right`}>{v}</span>
  </div>
);
